import ContainerSimple from "../../../Components/ContainerSimple";
import Custom from "../../../Components/Buttons/Custom";
import InputPayment from "../../../Components/InputFields/InputPayment";
import SVG from "../../../Components/SVG";
import { useState, useEffect } from "react";
import { isoToJalali } from "../../../helper/isoToJalali";
import { API_BASE_URL } from "../../../config/api";

const REFRESH_ENDPOINT = `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;
const DEFAULT_WITHDRAW_MODAL_CONFIG = {
  transactionLabel: "برداشت از حساب",
  getDetailUrl: (code) =>
    `${API_BASE_URL}/api/v0/payment-payout/admin/payouts/${code}/`,
  getDecisionUrl: (id) =>
    `${API_BASE_URL}/api/v0/payment-payout/admin/payout-decision/${id}/`,
};

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

function normalizeStatus(status) {
  const value = String(status || "").toLowerCase();

  if (value === "failed") return "rejected";
  if (value === "completed") return "approved";
  return value;
}

async function authorizedFetch(url, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  };

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status !== 401) {
    return response;
  }

  const refreshResponse = await fetch(REFRESH_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refresh: localStorage.getItem("refresh"),
    }),
  });

  if (!refreshResponse.ok) {
    logoutAndRedirect();
    return null;
  }

  const refreshData = await refreshResponse.json();
  localStorage.setItem("access", refreshData.access);

  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
}

export default function WithdrawModal({
  onClose,
  withdrawCode,
  modalConfig = DEFAULT_WITHDRAW_MODAL_CONFIG,
}) {
  const transactionLabel =
    modalConfig.transactionLabel ??
    DEFAULT_WITHDRAW_MODAL_CONFIG.transactionLabel;
  const getDetailUrl =
    modalConfig.getDetailUrl ?? DEFAULT_WITHDRAW_MODAL_CONFIG.getDetailUrl;
  const getDecisionUrl =
    modalConfig.getDecisionUrl ?? DEFAULT_WITHDRAW_MODAL_CONFIG.getDecisionUrl;

  const [withdrawData, setWithdrawData] = useState(null);
  const [status, setStatus] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isEditable = withdrawData?.status === "pending";

  const handleCopy = async (e) => {
    const p = e.currentTarget.querySelector("p");
    if (!p) return;
    const text = p.innerText;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  };

  useEffect(() => {
    const fetchWithdrawData = async () => {
      if (!withdrawCode) {
        return;
      }

      try {
        const response = await authorizedFetch(getDetailUrl(withdrawCode), {
          method: "GET",
        });

        if (!response) return;

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const normalizedData = {
          ...data,
          status: normalizeStatus(data?.status),
        };

        console.log("Withdraw Data:", normalizedData);
        setWithdrawData(normalizedData);
        setStatus(
          normalizedData.status === "pending" ? "" : normalizedData.status,
        );
        setActionError("");
      } catch (error) {
        console.error("Error fetching withdraw data:", error);
      }
    };

    fetchWithdrawData();
  }, [getDetailUrl, withdrawCode]);

  const handleChange = async () => {
    if (!withdrawData || withdrawData.status !== "pending") {
      onClose();
      return;
    }

    if (status === withdrawData.status) {
      onClose();
      return;
    }

    if (status !== "approved" && status !== "rejected") {
      setActionError("وضعیت باید تایید شده یا رد شده باشد.");
      return;
    }

    setActionError("");
    setSubmitting(true);

    try {
      const response = await authorizedFetch(getDecisionUrl(withdrawData.id), {
        method: "POST",
        body: JSON.stringify({
          status: status === "approved" ? "completed" : "rejected",
        }),
      });

      if (!response) return;

      let data = null;
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await response.json();
      }

      if (!response.ok) {
        setActionError(data?.detail || `HTTP ${response.status}`);
        return;
      }

      window.location.reload();
    } catch (error) {
      setActionError(error?.message || "ثبت تغییرات با خطا مواجه شد.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContainerSimple>
      <div className="w-full rounded-lg border border-zi-border-dark flex overflow-hidden">
        <div className="w-max bg-black/10 text-black/50 dark:text-zi-text-dark/50 divide-y divide-black/15 dark:divide-white/15 [&>*]:px-2.5 [&>*]:py-2 [&>*]:whitespace-nowrap">
          <p>کد تراکنش</p> <p>نوع تراکنش</p> <p>تاریخ ایجاد</p> <p>نام کاربر</p>
          <p>حساب مرتبط</p>
          <p>مبلغ درخواست</p>
          <p>شبکه</p>
          <p>آدرس مقصد</p>
          <p>تاریخ تکمیل</p>
          <p className="bg-black/15 !border-t-2 !border-black dark:!border-zi-border-dark">
            وضعیت
          </p>{" "}
        </div>
        <div className="flex-1 text-zi-text-light dark:text-zi-text-dark divide-y divide-black/15 dark:divide-white/15 [&>*]:px-2.5 [&>*]:py-2 overflow-x-hidden [&>*]:w-full [&>*]:overflow-x-auto [&>*]:whitespace-nowrap">
          <p>{withdrawData?.code ?? "-"}</p> <p>{transactionLabel}</p>{" "}
          <p>
            {withdrawData?.created_at
              ? isoToJalali(withdrawData.created_at)
              : "-"}
          </p>{" "}
          <p>
            {withdrawData?.user_full_name
              ? withdrawData.user_full_name
              : withdrawData?.user_email
                ? withdrawData.user_email
                : "-"}
          </p>
          <p>
            {withdrawData?.account_code ?? withdrawData?.related_account ?? "-"}
          </p>
          <p>{withdrawData?.amount ? `$${withdrawData.amount}` : "-"}</p>
          <p>{withdrawData?.network ?? "-"}</p>
          <div
            className="flex items-center gap-2.5 cursor-pointer"
            onClick={handleCopy}
          >
            <p className="w-full">{withdrawData?.wallet_address ?? "-"}</p>
            <span>
              <SVG
                name="copy"
                classname="stroke-zi-text-light dark:stroke-zi-text-dark"
              />
            </span>
          </div>
          <p>
            {withdrawData?.status !== "pending" && withdrawData?.updated_at
              ? isoToJalali(withdrawData.updated_at)
              : "-"}
          </p>
          <InputPayment
            type="select"
            options={["تایید شده", "رد شده"]}
            name="status"
            id="status"
            placeholder="انتخاب کنید"
            className="w-full !p-0 !border-t-2 !border-black dark:!border-zi-border-dark"
            svgName="select"
            svgClassName="size-3 stroke-zi-text-light dark:stroke-zi-text-dark"
            inputClassName="w-full cursor-pointer px-2.5 py-2"
            state={status}
            setState={setStatus}
            disabled={!isEditable || submitting}
          />
        </div>
      </div>

      {actionError && (
        <p className="mt-3 text-sm text-red-500">{actionError}</p>
      )}

      <div className="w-full flex gap-2.5 mt-6">
        {isEditable && (
          <Custom
            text={submitting ? "در حال ثبت..." : "ذخیره تغییرات"}
            svg_name="checkMark"
            svgClassName="stroke-zi-text-dark"
            href=""
            className={`!rounded-lg !p-2.5 w-full text-zi-text-dark bg-zi-gradient-button-blue ${
              submitting ? "pointer-events-none opacity-60" : ""
            }`}
            textShadow={true}
            svgShadow={true}
            onClick={handleChange}
          />
        )}
        <Custom
          text="بستن"
          svg_name="xMark"
          svgClassName="stroke-zi-text-light dark:stroke-zi-text-dark"
          href=""
          className={`!rounded-lg !p-2.5 w-full text-zi-text-light dark:text-zi-text-dark ring-1 ring-zi-border-dark bg-transparent ${
            submitting ? "pointer-events-none opacity-60" : ""
          }`}
          onClick={onClose}
        />
      </div>
    </ContainerSimple>
  );
}
